import { useEffect, useState } from "react";
import {
  getDatabase,
  ref,
  push,
  set,
  onValue,
  update,
  remove,
} from "firebase/database";
import { MapPinned, Pencil, Trash2, X } from "lucide-react";
import "./routes.css";

const Routes = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [routes, setRoutes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [editingRoute, setEditingRoute] = useState(null);

  useEffect(() => {
    const db = getDatabase();
    const routesRef = ref(db, "transport-routes");

    const unsubscribe = onValue(
      routesRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setRoutes([]);
          setLoading(false);
          return;
        }

        const data = snapshot.val();

        const list = Object.entries(data).map(([id, route]) => ({
          id,
          ...route,
        }));

        list.sort(
          (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)
        );

        setRoutes(list);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching transport routes:", err);
        setError("Failed to fetch routes.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanFrom = from.trim();
    const cleanTo = to.trim();

    setSuccess("");
    setError("");

    if (!cleanFrom || !cleanTo) {
      setError("Please enter both From and To locations.");
      return;
    }

    try {
      setSaving(true);

      const db = getDatabase();

      if (editingRoute) {
        const routeRef = ref(db, `transport-routes/${editingRoute.id}`);

        await update(routeRef, {
          from: cleanFrom,
          to: cleanTo,
          updatedAt: Date.now(),
        });

        setSuccess("Route updated successfully.");
        setEditingRoute(null);
      } else {
        const routesRef = ref(db, "transport-routes");
        const newRouteRef = push(routesRef);

        await set(newRouteRef, {
          createdAt: Date.now(),
          from: cleanFrom,
          to: cleanTo,
        });

        setSuccess("Route created successfully.");
      }

      setFrom("");
      setTo("");
    } catch (err) {
      console.error("Error saving transport route:", err);
      setError("Failed to save route. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (route) => {
    setEditingRoute(route);
    setFrom(route.from || "");
    setTo(route.to || "");
    setSuccess("");
    setError("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleCancelEdit = () => {
    setEditingRoute(null);
    setFrom("");
    setTo("");
    setSuccess("");
    setError("");
  };

  const handleDelete = async (route) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the route "${route.from || "-"} → ${
        route.to || "-"
      }"?`
    );

    if (!confirmed) return;

    try {
      setSuccess("");
      setError("");

      const db = getDatabase();
      const routeRef = ref(db, `transport-routes/${route.id}`);

      await remove(routeRef);

      if (editingRoute?.id === route.id) {
        handleCancelEdit();
      }

      setSuccess("Route deleted successfully.");
    } catch (err) {
      console.error("Error deleting transport route:", err);
      setError("Failed to delete route. Please try again.");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";

    const date = new Date(Number(timestamp));

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="tr-container">
      {/* CREATE / EDIT FORM */}
      <div className="tr-form-card">
        <div className="tr-form-header">
          <MapPinned size={24} color="#222" strokeWidth={2} />

          <div>
            <h2 className="tr-form-title">
              {editingRoute ? "Edit Transport Route" : "Create Transport Route"}
            </h2>
            <p className="tr-form-subtitle">
              {editingRoute
                ? "Update the selected transport route."
                : "Add a new route to Firebase."}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* FROM */}
          <div className="tr-form-group">
            <label htmlFor="route-from" className="tr-label">
              From
            </label>
            <input
              id="route-from"
              type="text"
              className="tr-input"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="Enter starting location"
              maxLength={400}
              disabled={saving}
            />
          </div>

          {/* TO */}
          <div className="tr-form-group">
            <label htmlFor="route-to" className="tr-label">
              To
            </label>
            <input
              id="route-to"
              type="text"
              className="tr-input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="Enter destination"
              maxLength={400}
              disabled={saving}
            />
          </div>

          {/* ERROR */}
          {error && <div className="tr-error">{error}</div>}

          {/* SUCCESS */}
          {success && <div className="tr-success">{success}</div>}

          {/* BUTTONS */}
          <div className="tr-btn-row">
            <button
              type="submit"
              className="tr-btn-primary"
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : editingRoute
                ? "Update Route"
                : "Create Route"}
            </button>

            {editingRoute && (
              <button
                type="button"
                className="tr-btn-cancel"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                <X size={16} color="#333" strokeWidth={2} />
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ROUTES TABLE */}
      <div className="tr-table-card">
        <div className="tr-table-header">
          <div>
            <h3 className="tr-table-title">Created Routes</h3>
            <p className="tr-table-subtitle">
              Total Routes: <b>{routes.length}</b>
            </p>
          </div>
        </div>

        {loading ? (
          <div className="tr-empty-state">Loading routes...</div>
        ) : routes.length === 0 ? (
          <div className="tr-empty-state">No routes found.</div>
        ) : (
          <div className="tr-table-wrapper">
            <table className="tr-table">
              <thead>
                <tr>
                  <th className="tr-th">From</th>
                  <th className="tr-th">To</th>
                  <th className="tr-th">Created At</th>
                  <th className="tr-th">Actions</th>
                </tr>
              </thead>

              <tbody>
  {routes.map((route) => (
    <tr key={route.id}>
      <td className="tr-td">{route.from || "-"}</td>

      <td className="tr-td">{route.to || "-"}</td>

      <td className="tr-td">
        {formatDate(route.createdAt)}
      </td>

      <td className="tr-td">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {/* EDIT BUTTON */}
          <button
            type="button"
            onClick={() => handleEdit(route)}
            title="Edit route"
            aria-label="Edit route"
            style={{
              width: 34,
              height: 34,
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #bfdbfe",
              borderRadius: 6,
              background: "#eff6ff",
              color: "#2563eb",
              cursor: "pointer",
            }}
          >
            <Pencil
              size={17}
              color="#2563eb"
              strokeWidth={2.5}
            />
          </button>

          {/* DELETE BUTTON */}
          <button
            type="button"
            onClick={() => handleDelete(route)}
            title="Delete route"
            aria-label="Delete route"
            style={{
              width: 34,
              height: 34,
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #fecaca",
              borderRadius: 6,
              background: "#fef2f2",
              color: "#dc2626",
              cursor: "pointer",
            }}
          >
            <Trash2
              size={17}
              color="#dc2626"
              strokeWidth={2.5}
            />
          </button>
        </div>
      </td>
    </tr>
  ))}
</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Routes;